import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

/**
 * HeroFlipbook — replaces the previous SVG-mockup rectangles with real
 * AI-generated premium images that live-blend into the maroon hero
 * background (soft radial mask, no hard gold border, floating shadow).
 *
 * User-quote goal: "image ऐसा होना चाहिए कि website के साथ पूरी तरीके से
 * merge हो जाए और बढ़िया सा लगे".
 */
const HeroFlipbook = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  const PAGES = [
    { src: '/generated/karma-kundali-book.webp',
      hi: 'आपकी कर्म कुंडली book', en: 'Your Karma Kundali book',
      subHi: 'Cover · 55+ पेज premium', subEn: 'Cover · 55+ premium pages' },
    { src: '/generated/vedic-birth-chart.webp',
      hi: 'जन्म कुंडली चार्ट', en: 'Your Vedic Birth Chart',
      subHi: 'Page 6 · Lagna + ग्रह स्थिति', subEn: 'Page 6 · Lagna + planets' },
    { src: '/generated/dasha-timeline.webp',
      hi: 'महादशा roadmap', en: 'Mahadasha Roadmap',
      subHi: 'Page 22 · अगले 20 वर्ष', subEn: 'Page 22 · next 20 years' },
    { src: '/generated/astrologer-desk.webp',
      hi: 'व्यक्तिगत उपाय', en: 'Personal Remedies',
      subHi: 'Page 48 · मंत्र · रत्न · दान', subEn: 'Page 48 · mantras · gems · daan' },
  ];

  const next = useCallback(() => setPage((p) => (p + 1) % PAGES.length), [PAGES.length]);
  const prev = () => setPage((p) => (p - 1 + PAGES.length) % PAGES.length);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 4000);
    return () => clearInterval(id);
  }, [paused, next]);

  const active = PAGES[page];

  return (
    <div
      className="relative max-w-md mx-auto select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-flipbook"
    >
      {/* Soft gold radial glow that MERGES the image into the maroon hero */}
      <div
        className="absolute -inset-14 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.08) 40%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />

      {/* Image stage — no hard border, soft rounded corners, floating shadow */}
      <div className="relative aspect-[4/5]">
        {PAGES.map((p, i) => (
          <img
            key={i}
            src={p.src}
            alt={isHi ? p.hi : p.en}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            className={`absolute inset-0 w-full h-full object-cover rounded-2xl transition-all duration-[900ms] ease-out ${
              i === page
                ? 'opacity-100 scale-100 translate-y-0 z-10'
                : 'opacity-0 scale-[0.97] translate-y-2 z-0 pointer-events-none'
            }`}
            style={{
              boxShadow:
                i === page
                  ? '0 24px 56px -12px rgba(0,0,0,0.55), 0 0 0 1px rgba(243,229,171,0.18)'
                  : 'none',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, #000 62%, rgba(0,0,0,0.85) 82%, rgba(0,0,0,0) 100%)',
              maskImage:
                'radial-gradient(ellipse at center, #000 62%, rgba(0,0,0,0.85) 82%, rgba(0,0,0,0) 100%)',
            }}
            aria-hidden={i !== page}
          />
        ))}
      </div>

      {/* Caption — merges softly below the image */}
      <div className="relative mt-4 text-center px-2">
        <p className={`font-playfair font-bold text-lg text-brand-gold-light leading-tight ${isHi ? 'font-hindi-serif' : ''}`}>
          {isHi ? active.hi : active.en}
        </p>
        <p className={`text-brand-gold-light/70 text-xs mt-1 tracking-wide ${isHi ? 'font-hindi' : 'font-outfit'}`}>
          {isHi ? active.subHi : active.subEn}
        </p>
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={prev}
          aria-label="Previous"
          data-testid="flipbook-prev"
          className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border border-brand-gold/40 text-brand-gold-light hover:bg-brand-gold hover:text-brand-maroon-dark transition-all flex items-center justify-center"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2">
          {PAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i)}
              aria-label={`Preview ${i + 1}`}
              data-testid={`flipbook-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === page ? 'w-7 bg-brand-gold' : 'w-1.5 bg-brand-gold/40 hover:bg-brand-gold/70'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={next}
          aria-label="Next"
          data-testid="flipbook-next"
          className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border border-brand-gold/40 text-brand-gold-light hover:bg-brand-gold hover:text-brand-maroon-dark transition-all flex items-center justify-center"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Hint label */}
      <div className="mt-2 text-center">
        <span className={`inline-flex items-center gap-1.5 text-[11px] text-brand-gold-light/60 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
          <BookOpen className="w-3 h-3" />
          {isHi ? 'रिपोर्ट की झलक — auto-flip' : 'Report preview — auto-flip'}
        </span>
      </div>
    </div>
  );
};

export default HeroFlipbook;
